import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ShowIfHasAccess from "@/components/ShowIfHasAccess/ShowIfHasAccess";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { AlertCircle, Users, Loader2, Lock } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import VoucherRedemption from "@/modules/eventRegistration/features/EventRegistration/components/VoucherRedemption/VoucherRedemption";
import DiscountCodeRedemption from "@/modules/eventRegistration/features/EventRegistration/components/DiscountCodeRedemption/DiscountCodeRedemption";
import AdminDiscountForm from "@/modules/eventRegistration/features/EventRegistration/components/AdminDiscountForm/AdminDiscountForm";
import OrderSummaryStep from "@/modules/eventRegistration/features/EventRegistration/components/CheckoutStep/OrderSummaryStep";
import ZeroCostPaymentPanel from "@/modules/eventRegistration/features/EventRegistration/components/ZeroCostPaymentPanel/ZeroCostPaymentPanel";
import PaymentFormStep from "@/modules/eventRegistration/features/EventRegistration/components/CheckoutStep/PaymentFormStep";
import {
  calculateCheckoutPrice,
  type DiscountInfo,
} from "@/modules/eventRegistration/features/EventRegistration/utils/checkoutPriceCalculator";
import { useRegistration } from "@/modules/eventRegistration/features/EventRegistration/context/RegistrationContext";
import { completeEventRegistration } from "@/modules/eventRegistration/features/EventRegistration/api/completeEventRegistration/completeEventRegistrationApi";
import type { CompleteEventRegistrationRequest } from "@/modules/eventRegistration/features/EventRegistration/api/completeEventRegistration/types";
import {
  computeAvailabilityChanges,
  buildAvailabilityChangeMessages,
  type AvailabilityChangeData,
} from "@/modules/eventRegistration/features/EventRegistration/utils/availabilityUtils";

function CheckoutStep() {
  const navigate = useNavigate();
  const {
    eventInfo,
    eventSessionUuid,
    contactName,
    contactEmail,
    contactPhone,
    groupNotes,
    attendees,
    voucherQuantity,
    discountCode,
    discountValueType,
    discountRawValue,
    adminDiscountType,
    adminDiscountValue,
    adminDiscountReason,
    applyVoucher,
    removeVoucher,
    applyDiscountCode,
    removeDiscountCode,
    applyAdminDiscount,
    removeAdminDiscount,
    setAttendees,
    updateAvailableSeats,
  } = useRegistration();

  const hasNavigatedRef = useRef(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showChangesDialog, setShowChangesDialog] = useState(false);
  const [availabilityChanges, setAvailabilityChanges] = useState<string[]>([]);
  const [availabilityMessage, setAvailabilityMessage] = useState<string | null>(
    null,
  );
  const [changeData, setChangeData] = useState<AvailabilityChangeData | null>(
    null,
  );
  const [actualAvailableSeats, setActualAvailableSeats] = useState<
    number | null
  >(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!eventInfo || attendees.length === 0) return null;

  const eventName = eventInfo.eventName ?? "Untitled Event";
  const eventPrice = eventInfo.eventPrice ?? 0;
  const availableSeats = eventInfo.availableSeats ?? 0;
  const isCoveredByTotalAccess = eventInfo.isCoveredByTotalAccess ?? false;
  const availableVoucherSeats = eventInfo.companyAvailableVoucherSeats || 0;
  const totalCapacity = eventInfo.totalCapacity ?? 0;

  const pricingAttendees = attendees.map((a) => ({
    isSelected: a.isSelected,
    isWaitlist: a.isWaitlist,
  }));

  const codeDiscountInfo: DiscountInfo | null =
    discountCode && discountValueType
      ? { type: discountValueType, value: discountRawValue }
      : null;
  const adminDiscountInfo: DiscountInfo | null = adminDiscountType
    ? { type: adminDiscountType, value: adminDiscountValue }
    : null;

  const currentPricing = calculateCheckoutPrice(
    pricingAttendees,
    eventPrice,
    voucherQuantity,
    codeDiscountInfo,
    adminDiscountInfo,
  );

  const hasWaitlistAttendees = currentPricing.waitlistedAttendeeCount > 0;
  const hasPaidAttendees = currentPricing.paidAttendeeCount > 0;
  const shouldShowPaymentForm = currentPricing.total > 0;
  const shouldShowZeroCost = currentPricing.total === 0;

  const hasAnyDiscountOption =
    (hasPaidAttendees && availableVoucherSeats > 0) ||
    (hasPaidAttendees && eventInfo.discounts && eventInfo.discounts.length > 0);

  function buildRequestPayload(
    dataDescriptor: string,
    dataValue: string,
    amount: number,
    invoiceNumber: string,
    cardExpiration?: string,
  ): CompleteEventRegistrationRequest {
    return {
      eventSessionUuid,
      contactName,
      contactEmail,
      contactPhone: contactPhone || undefined,
      groupNotes: groupNotes || undefined,
      attendees: attendees
        .filter((a) => a.isSelected)
        .map((a) => ({
          firstName: a.firstName,
          lastName: a.lastName,
          email: a.email,
          isSelected: a.isSelected,
          specialRequests: a.specialRequests,
        })),
      dataDescriptor,
      dataValue,
      amount,
      invoiceNumber,
      cardExpiration: cardExpiration || undefined,
      voucherQuantity: voucherQuantity || undefined,
      discountCode: discountCode || undefined,
      discountAmount:
        currentPricing.codeDiscount + currentPricing.adminDiscount || undefined,
      adminDiscountType: adminDiscountType || undefined,
      adminDiscountValue: adminDiscountValue || undefined,
      adminDiscountReason: adminDiscountReason || undefined,
    };
  }

  async function handleCompleteRegistration() {
    setIsProcessing(true);
    try {
      const invoiceNumber = `INV${Date.now()}`;
      const payload = buildRequestPayload("FREE", "FREE", 0, invoiceNumber);
      payload.expectedAvailableSeats = availableSeats;

      const response = await completeEventRegistration(payload);

      if (
        response.data.requiresConfirmation &&
        response.data.actualAvailableSeats !== undefined
      ) {
        const newAvailableSeats = response.data.actualAvailableSeats;
        const selectedCount = attendees.filter((a) => a.isSelected).length;
        const changes = computeAvailabilityChanges(
          selectedCount,
          availableSeats,
          newAvailableSeats,
        );
        const messages = buildAvailabilityChangeMessages(
          availableSeats,
          newAvailableSeats,
        );
        setChangeData(changes);
        setActualAvailableSeats(newAvailableSeats);
        setAvailabilityChanges(messages.changes);
        setAvailabilityMessage(messages.message);
        setShowChangesDialog(true);
        setIsProcessing(false);
        return;
      }

      if (response.data.success && response.data.eventCheckoutUuid) {
        navigate(
          `/event-registration/events/${response.data.eventCheckoutUuid}/confirmation`,
        );
      }
    } catch {
      setIsProcessing(false);
    }
  }

  function handleAcceptAvailabilityChanges() {
    if (!changeData || actualAvailableSeats === null) return;

    const selectedAttendees = attendees.filter((a) => a.isSelected);
    const nonSelectedAttendees = attendees.filter((a) => !a.isSelected);
    const updatedSelected = selectedAttendees.map((a, i) => ({
      ...a,
      isWaitlist: i >= changeData.newSeatedCount,
    }));
    setAttendees([...updatedSelected, ...nonSelectedAttendees]);
    updateAvailableSeats(actualAvailableSeats);

    setShowChangesDialog(false);
    setChangeData(null);
    setActualAvailableSeats(null);
    setIsProcessing(false);
  }

  function handlePaymentSuccess(eventCheckoutUuid: string) {
    hasNavigatedRef.current = true;
    navigate(`/event-registration/events/${eventCheckoutUuid}/confirmation`);
  }

  const handleCancelAfterChanges = () => {
    setShowChangesDialog(false);
    setChangeData(null);
    setActualAvailableSeats(null);
  };

  const pricingAfterChanges = changeData
    ? calculateCheckoutPrice(
        attendees
          .filter((a) => a.isSelected)
          .map((_, i) => ({
            isSelected: true,
            isWaitlist: i >= changeData.newSeatedCount,
          })),
        eventPrice,
        voucherQuantity,
        codeDiscountInfo,
        adminDiscountInfo,
      )
    : null;

  return (
    <>
      <div>
        {/* Total Access banner */}
        {isCoveredByTotalAccess && (
          <div className="mb-6 rounded-md bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 px-4 py-3">
            <p className="text-sm text-blue-700 dark:text-blue-300">
              Included with your Total Access subscription — no charge for this
              event.
            </p>
          </div>
        )}

        {/* Waitlist note */}
        {hasWaitlistAttendees && (
          <p className="mb-6 text-sm text-muted-foreground">
            {currentPricing.waitlistedAttendeeCount ===
            attendees.filter((a) => a.isSelected).length
              ? "All attendees will be waitlisted — no charge."
              : `${currentPricing.waitlistedAttendeeCount} of ${attendees.filter((a) => a.isSelected).length} attendees will be waitlisted (no charge).`}
          </p>
        )}

        {/* Two-column layout: payment left, summary right */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Left column — payment flow */}
          <div className="lg:col-span-3">
            {/* Discounts & vouchers — compact collapsible section */}
            {!isCoveredByTotalAccess &&
              (hasAnyDiscountOption || hasPaidAttendees) && (
                <div className="space-y-1 mb-6 pb-6 border-b border-border">
                  {hasPaidAttendees && availableVoucherSeats > 0 && (
                    <VoucherRedemption
                      attendeeCount={
                        currentPricing.paidAttendeeCount +
                        currentPricing.waitlistedAttendeeCount
                      }
                      availableVoucherSeats={availableVoucherSeats}
                      isVoucherApplied={voucherQuantity > 0}
                      onVoucherApplied={(quantity: number) => {
                        applyVoucher(quantity);
                      }}
                      onVoucherRemoved={() => {
                        removeVoucher();
                      }}
                    />
                  )}
                  {hasPaidAttendees &&
                    eventInfo.discounts &&
                    eventInfo.discounts.length > 0 && (
                      <DiscountCodeRedemption
                        allAvailableDiscounts={eventInfo.discounts}
                        appliedDiscountInfo={
                          discountCode && discountValueType
                            ? {
                                code: discountCode,
                                type: discountValueType,
                                value: discountRawValue,
                              }
                            : null
                        }
                        isDiscountApplied={discountCode !== null}
                        onDiscountApplied={(discountData) => {
                          applyDiscountCode(
                            discountData.code,
                            discountData.type,
                            discountData.value,
                          );
                        }}
                        onDiscountRemoved={() => {
                          removeDiscountCode();
                        }}
                      />
                    )}
                  {hasPaidAttendees && (
                    <ShowIfHasAccess
                      requiredPermissions={["administration-events"]}
                    >
                      <AdminDiscountForm
                        appliedDiscountInfo={
                          adminDiscountType
                            ? {
                                type: adminDiscountType,
                                value: adminDiscountValue,
                              }
                            : null
                        }
                        eventPrice={eventPrice}
                        isDiscountApplied={adminDiscountType !== null}
                        onDiscountApplied={(discountData) => {
                          applyAdminDiscount(
                            discountData.discount_type,
                            discountData.discount_value,
                            discountData.reason,
                          );
                        }}
                        onDiscountRemoved={() => {
                          removeAdminDiscount();
                        }}
                      />
                    </ShowIfHasAccess>
                  )}
                </div>
              )}

            {/* Payment form or zero-cost panel */}
            {shouldShowPaymentForm && (
              <PaymentFormStep
                buildRequestPayload={buildRequestPayload}
                disabled={false}
                handlePaymentSuccess={handlePaymentSuccess}
                isProcessing={isProcessing}
                setIsProcessing={setIsProcessing}
                total={currentPricing.total}
              />
            )}
            {shouldShowZeroCost &&
              !hasAnyDiscountOption &&
              !hasPaidAttendees && (
                // When there are no discount options at all and left column is empty,
                // show a simple message so the left column isn't blank
                <p className="text-sm text-muted-foreground">
                  No payment required. Complete your registration using the
                  button on the right.
                </p>
              )}
          </div>

          {/* Right column — order summary, sticky */}
          <div className="lg:col-span-2">
            <div className="lg:sticky lg:top-24">
              <OrderSummaryStep
                adminDiscountAmount={currentPricing.adminDiscount}
                adminDiscountPerSeat={currentPricing.adminDiscountPerSeat}
                availableSeats={availableSeats}
                codeDiscountAmount={currentPricing.codeDiscount}
                codeDiscountPerSeat={currentPricing.codeDiscountPerSeat}
                eventName={eventName}
                eventPrice={eventPrice}
                paidAttendeeCount={currentPricing.paidAttendeeCount}
                paidSeatsAfterVoucher={currentPricing.paidSeatsAfterVoucher}
                total={currentPricing.total}
                totalCapacity={totalCapacity}
                voucherSeatsUsed={voucherQuantity}
                waitlistedAttendeeCount={currentPricing.waitlistedAttendeeCount}
              >
                {/* CTA button — always under the total */}
                {shouldShowZeroCost && (
                  <ZeroCostPaymentPanel
                    isAdminDiscountApplied={adminDiscountType !== null}
                    isDiscountApplied={discountCode !== null}
                    isProcessing={isProcessing}
                    isVoucherApplied={voucherQuantity > 0}
                    onCompleteRegistration={handleCompleteRegistration}
                  />
                )}
                {shouldShowPaymentForm && (
                  <div>
                    <Button
                      className="w-full"
                      disabled={isProcessing}
                      form="payment-form"
                      type="submit"
                    >
                      {isProcessing ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        `Pay $${currentPricing.total.toFixed(2)}`
                      )}
                    </Button>
                    <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground mt-2">
                      <Lock className="h-3 w-3" />
                      <span>Secure, encrypted payment</span>
                    </div>
                  </div>
                )}
              </OrderSummaryStep>
            </div>
          </div>
        </div>
      </div>

      {/* Availability changes dialog */}
      <Dialog onOpenChange={setShowChangesDialog} open={showChangesDialog}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-500" />
              Seat Availability Has Changed
            </DialogTitle>
            <DialogDescription className="pt-4">
              {availabilityMessage && (
                <Alert className="mb-4">
                  <AlertDescription>{availabilityMessage}</AlertDescription>
                </Alert>
              )}
              {changeData && pricingAfterChanges && (
                <div className="space-y-4">
                  <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg space-y-3">
                    <h4 className="font-medium flex items-center">
                      <Users className="h-4 w-4 mr-2" />
                      Attendee Changes
                    </h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">Previously</p>
                        <p className="font-medium">
                          {changeData.previousSeatedCount} paid seats
                        </p>
                        {changeData.previousWaitlistCount > 0 && (
                          <p className="text-amber-600">
                            {changeData.previousWaitlistCount} waitlisted
                          </p>
                        )}
                      </div>
                      <div>
                        <p className="text-muted-foreground">Now</p>
                        <p className="font-medium">
                          {changeData.newSeatedCount} paid seats
                        </p>
                        {changeData.newWaitlistCount > 0 && (
                          <p className="text-amber-600">
                            {changeData.newWaitlistCount} waitlisted
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {pricingAfterChanges.total !== currentPricing.total && (
                    <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg space-y-2">
                      <h4 className="font-medium">Price Update</h4>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">
                            Previous total:
                          </span>
                          <span className="line-through">
                            ${currentPricing.total.toFixed(2)}
                          </span>
                        </div>
                        <Separator className="my-1" />
                        <div className="flex justify-between font-medium">
                          <span>New total:</span>
                          <span className="text-lg">
                            ${pricingAfterChanges.total.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {availabilityChanges.length > 0 && (
                    <div className="space-y-2">
                      <p className="font-medium text-sm">What happened:</p>
                      <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                        {availabilityChanges.map((change, index) => (
                          <li key={index}>{change}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button onClick={handleCancelAfterChanges} variant="outline">
              Cancel
            </Button>
            <Button onClick={handleAcceptAvailabilityChanges}>
              Accept Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default CheckoutStep;
